import React from 'react';
import { Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import { styled } from '@mui/system';

const colors = {
  primary: {
    main: '#995A00',
    nuetral: '#8C8C8C',
    hover: '#666666',
    disabled: '#D9D9D9',
    text: '#666666',
  },
  error: {
    main: '#CC0000',

  }
};

const StyledRadio = styled(Radio)(({ theme, color = 'primary', size = 'medium' }) => {
  const sizeStyles = {
    small: {
      '& .MuiSvgIcon-root': { fontSize: 18 },
      '&.MuiRadio-root': { padding: 6 }
    },
    medium: {
      '& .MuiSvgIcon-root': { fontSize: 20 },
      '&.MuiRadio-root': { padding: 8 }
    },
    large: {
      '& .MuiSvgIcon-root': { fontSize: 24 },
      '&.MuiRadio-root': { padding: 10 }
    }
  };

  return {
    color: colors.primary.nuetral,
    padding: 8,

    '&:hover': {
        color:  colors.primary.hover,
        backgroundColor: '#00000000',
    },
    '&.Mui-checked': {
      color: colors.primary.main,
        // '&:hover': {
        //     color:  colors.primary.hover,
        //     backgroundColor: '#00000000',
        // },
    },
    '&.Mui-disabled': {
        cursor: 'not-allowed',
        '&.Mui-checked': {
            color: colors.primary.disabled,
        },
    },
    '&.Mui-error': {
        color: colors.error.main,
        '&.Mui-checked': {
            color: colors.error.main,
        },
    },
    transition: theme.transitions.create(['color', 'background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    ...sizeStyles[size],
  };
});

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, labelPlacement = 'end', size = 'medium' }) => {
  const labelSizeStyles = {
    small: {
      fontSize: '13px',
      lineHeight: '20px',
    },
    medium: {
      fontSize: '16px',
      lineHeight: '24px',
    },
    large: {
      fontSize: '16px',
      lineHeight: '24px',
    }
  };

  return ({
    margin: 0,
    '& .MuiFormControlLabel-label': {
        ...labelSizeStyles[size],
        color: '#333333',
        fontWeight: 600,
        userSelect: 'none',
    },
    '&.Mui-disabled': {
        '& .MuiFormControlLabel-label' : {
            color: colors.primary.nuetral,
        },
    },
    '&.Mui-error': {
        color: colors.error.main,
        '& .MuiFormControlLabel-label' : {
        color: colors.error.main,
        },
        '&.Mui-checked': {
        color: colors.error.main,
        },
    },
  });
});

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: '#333333',
  marginBottom: 8,
  '&.Mui-focused': {
    color: colors.primary.main,
  },
  '&.Mui-error': {
    color: colors.error.main,
  },
  '&.Mui-disabled': {
    color: colors.primary.disabled,
  },
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme, row = false }) => ({
  display: 'flex',
  flexDirection: row ? 'row' : 'column',
  gap: row ? 16 : 4,
  '& .MuiFormControlLabel-root': {
    margin: 0,
  },
}));

// Single Radio Button Component
const CustomRadio = React.forwardRef(({
  label,
  checked,
  disabled = false,
  required = false,
  error = false,
  color = 'primary',
  size = 'medium',
  labelPlacement = 'end',
  onChange,
  name,
  value,
  id,
  className,
  sx,
  ...props
}, ref) => {
  const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;
  
  const radioElement = (
    <StyledRadio
      ref={ref}
      id={radioId}
      checked={checked}
      disabled={disabled}
      required={required}
      color={color}
      size={size}
      onChange={onChange}
      name={name}
      value={value}
      className={`${className || ''} ${error ? 'Mui-error' : ''}`}
      sx={sx}
      {...props}
    />
  );

  if (!label) {
    return radioElement;
  }

  return (
    <StyledFormControlLabel
      control={radioElement}
      label={label}
      labelPlacement={labelPlacement}
      disabled={disabled}
      size={size}
    />
  );
});

// Radio Group Component
const CustomRadioGroup = React.forwardRef(({
  label,
  options = [],
  value,
  defaultValue,
  name,
  onChange,
  disabled = false,
  required = false,
  error = false,
  helperText,
  color = 'primary',
  size = 'medium',
  labelPlacement = 'end',
  row = false,
  className,
  sx,
  ...props
}, ref) => {
  const groupId = `radio-group-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <FormControl 
      component="fieldset" 
      error={error} 
      disabled={disabled}
      required={required}
      className={className}
      sx={sx}
    >
      {label && (
        <StyledFormLabel component="legend" id={`${groupId}-label`}>
          {label}
        </StyledFormLabel>
      )}
      <StyledRadioGroup
        ref={ref}
        aria-labelledby={label ? `${groupId}-label` : undefined}
        name={name}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        row={row}
        {...props}
      >
        {options.map((option, index) => {
          // Handle both string and object options
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          const optionDisabled = typeof option === 'object' ? option.disabled : false;
          
          return (
            <StyledFormControlLabel
              key={optionValue || index}
              value={optionValue}
              control={
                <StyledRadio
                  color={color}
                  size={size}
                  className={error ? 'Mui-error' : ''}
                />
              }
              label={optionLabel}
              labelPlacement={labelPlacement}
              disabled={disabled || optionDisabled}
              size={size}
            />
          );
        })}
      </StyledRadioGroup>
      {helperText && (
        <div style={{ 
          fontSize: '12px', 
          color: error ? colors.error.main : colors.neutral.text,
          marginTop: 4 
        }}>
          {helperText}
        </div>
      )}
    </FormControl>
  );
});

CustomRadio.displayName = 'CustomRadio';
CustomRadioGroup.displayName = 'CustomRadioGroup';

export default CustomRadio;
export { CustomRadioGroup };
